import type {
    ProjectQueryParam,
    ProjectSelectorQueryParam,
    ProjectUpdatePriceForm
} from "../../../../common/types/project";
import httpRequest from "./index";
import { BaseService } from "@/api/baseService";
import type { Page, Project, ProjectExtend, ProjectStatisticsVo, ProjectVo } from "@zhihaoscm/entity";
import { pickBy as _pickBy, identity as _identity } from "lodash-es";

class ProjectService extends BaseService<Project, ProjectVo, ProjectQueryParam, Project> {
    public async getQuantity(params: { projectId: number; customerId?: number }): Promise<number> {
        params = _pickBy(params, _identity) as { projectId: number; customerId?: number };
        const { data } = await this.service.get(`${this.path}/findEstimateTon`, { params });
        return Number(data) >= 0 ? Number.parseInt(data) : 0;
    }

    /**
     * 分页查询
     */
    public override async paging(params: ProjectQueryParam): Promise<Page<ProjectVo>> {
        const enum isPCWeb {
            IS_PC = 1
        }
        params.isCustomPC = isPCWeb.IS_PC;
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }

    public async statistics(projectId: number, type: number, customerId?: number): Promise<ProjectStatisticsVo> {
        const { data } = await this.service.get(`${this.path}/statistics`, { params: { projectId, type, customerId } });
        return data;
    }

    public async updatePrice(params: Partial<ProjectUpdatePriceForm>): Promise<void> {
        const { data } = await this.service.put(`${this.path}/update-price`, params);
        return data;
    }

    public override async selector(params: ProjectSelectorQueryParam): Promise<Project[]> {
        const { data } = (await this.service.get(`${this.path}/selector`, { params })) as any;
        return data.list;
    }

    public async noPaging(params: ProjectSelectorQueryParam): Promise<Project[]> {
        // if(!params.name) return;
        const { data } = (await this.service.get(`${this.path}/no-paging/selector`, { params })) as any;
        return data;
    }

    public async findEstimatedAmount(data: { projectId: number; customerId?: number }): Promise<number> {
        data = _pickBy(data, _identity) as { projectId: number; customerId?: number };
        const { data: estimateTon } = (await this.service.get(`${this.path}/findEstimatedAmount`, {
            params: data
        })) as any;
        return Number(estimateTon) >= 0 ? Number(estimateTon) : 0;
    }

    /**查询采购商信息 */
    public async getBuyers(projectId: number): Promise<ProjectExtend[]> {
        const { data } = await this.service.get(`${this.path}/buyers`, { params: { projectId } });
        return data;
    }

    // 获取采购商可提货预估金额
    public async findBuyerEstimatedAmount(projectId: number, customerId?: number): Promise<number> {
        const { data } = await this.service.get(`${this.path}/findBuyerEstimatedAmount`, {
            params: { projectId, customerId }
        });
        return Number(data) >= 0 ? Number.parseInt(data) : 0;
    }

    // 获取采购商可提货吨数
    public async findBuyerEstimateTon(projectId: number, customerId?: number): Promise<number> {
        const { data } = await this.service.get(`${this.path}/findBuyerEstimateTon`, {
            params: { projectId, customerId }
        });
        return Number(data) >= 0 ? Number.parseInt(data) : 0;
    }

    /**分页查询项目列表-分身版 */
    public async channelPaging(params: ProjectQueryParam): Promise<Page<ProjectVo>> {
        const { data } = await this.service.get(`${this.path}/channel/paging`, { params });
        return data;
    }

    // 台账导出
    public async projectLedgerExport(params: { id: number; customerId?: number }): Promise<Blob> {
        // Vue 原实现向 Axios.get 传入三个参数；使用同一调用形态保持现有运行行为。
        return await (this.service.get as any)(
            `${this.path}/projectLedgerExport/${params.id}`,
            { params: { customerId: params.customerId } },
            {
                responseType: "blob",
                timeout: 600000,
                loading: true
            }
        );
    }
    // 购销商项目列表 联合购销商与供应商：3 联合购销商与采购商：4
    public async getFindBySupplyChainId(): Promise<Project[]> {
        const { data } = await this.service.get(this.path + "/findBySupplyChainId");
        return data;
    }
    // 供应商项目列表
    public async getFindBySupplierId(): Promise<Project[]> {
        const { data } = await this.service.get(this.path + "/findBySupplierId");
        return data;
    }
    // 采购商项目列表
    public async getFindByBuyerId(): Promise<Project[]> {
        const { data } = await this.service.get(this.path + "/findByBuyerId");
        return data;
    }
}

export const projectService = new ProjectService("/project", httpRequest);
